import React from 'react';
import { Platform, View } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import { initMercadoPago, Wallet } from '@mercadopago/sdk-react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { createPreference } from 'services/paymentsService';
import AppContext from 'contexts/AppContext';
import useChangeOrder from 'hooks/useChangeOrder';
import useChangeShoppingCart from 'hooks/useChangeShoppingCart';
import useChangePayment from 'hooks/useChangePayment';
import OrderContext from 'contexts/OrderContext';
import PaymentMPCheckoutScreenProps from 'types/screens/PaymentMPCheckoutScreenProps';
import constantsStorage from 'constants/Storage';
import { getCommerceByCodes } from 'services/commercesService';
import { getOrder } from 'services/ordersService';

export default ({ navigation, route }: PaymentMPCheckoutScreenProps): JSX.Element => {
  const { setIsLoading, setCurrentVenue, currentVenue, setCheckedForDifferentVenueCode } = React.useContext(AppContext);
  const {
    setCurrentShoppingCart,
    currentShoppingCart,
    selectedPaymentConfig,
    setCurrentOrder,
    currentOrder,
    setResultPayment,
    resultPayment,
    setCurrentCommerce,
  } = React.useContext(OrderContext);
  const [preferenceId, setPreferenceId] = React.useState<string | null>(null);
  const [paymentStatus, setPaymentStatus] = React.useState<string | null>(null);
  const [updatedBasicData, setUpdatedBasicData] = React.useState(false);
  const { storeCurrentShoppingCart } = useChangeShoppingCart();
  const { storeCurrentOrder, deleteCurrentOrder } = useChangeOrder();
  const { changeResultPayment } = useChangePayment();
  const params = route.params;
  const posibleMpPaymentRedirectStatus = params?.status;

  const setMPPreferenceData = async () => {
    const mercadoPagoCheckoutConfig = selectedPaymentConfig?.mercadoPagoCheckoutConfig;
    if (posibleMpPaymentRedirectStatus) {
      setIsLoading(true);
      setCheckedForDifferentVenueCode(true);
      setPaymentStatus(posibleMpPaymentRedirectStatus);
    } else {
      if (
        !currentShoppingCart ||
        !currentShoppingCart.processed ||
        currentShoppingCart.paid ||
        !currentOrder ||
        !mercadoPagoCheckoutConfig ||
        !currentVenue
      ) {
        if (currentShoppingCart?.paid) {
          await deleteCurrentOrder();
        }
        setTimeout(() => {
          navigation.reset({
            index: 0,
            routes: [{ name: 'HomeScreen' }],
          });
        }, 500);
        return;
      }
      await storeCurrentShoppingCart(currentShoppingCart);
      await storeCurrentOrder(currentOrder);

      setIsLoading(true);
      const preference = await createPreference(
        mercadoPagoCheckoutConfig?.id,
        currentShoppingCart?.detail,
        currentVenue?.code,
        currentShoppingCart.commerce.code,
        currentOrder.id,
      );
      if (preference) {
        setPreferenceId(preference);
        initMercadoPago(mercadoPagoCheckoutConfig.publicKey);
      } else {
        setIsLoading(false);
        navigation.reset({
          index: 1,
          routes: [
            { name: 'HomeScreen' },
            {
              name: 'ShoppingCartScreen',
              params:
                Platform.OS === 'web'
                  ? {
                      venueCode: currentVenue?.code,
                      commerceCode: currentShoppingCart.commerce.code,
                    }
                  : undefined,
            },
          ],
        });
      }
    }
  };

  const validateStorageData = async () => {
    const currentShoppingCart = await AsyncStorage.getItem(constantsStorage.currentShoppingCart);
    const currentOrder = await AsyncStorage.getItem(constantsStorage.currentOrder);
    return Boolean(currentShoppingCart) && Boolean(currentOrder);
  };

  useFocusEffect(
    React.useCallback(() => {
      setMPPreferenceData();
      return () => {
        if (resultPayment) {
          setResultPayment(null);
        }
      };
    }, []),
  );

  React.useEffect(() => {
    const handleResultPayment = async () => {
      if (resultPayment) {
        // Caso especial MP. En primer pago MPCHECKOUT limpia storage, si no hay data es primer pago. Si tiene datos en LS, continuar normalmente
        const storageDataIsOk = await validateStorageData();
        if (storageDataIsOk) {
          setUpdatedBasicData(false);
          changeResultPayment(resultPayment, false, params?.venueCode);
        } else {
          try {
            if (!params?.venueCode || !params?.commerceCode) {
              alert('Hubo un error al recuperar datos. Favor volver al inicio.');
              return;
            }
            const response = await getCommerceByCodes(params?.venueCode ?? '', params?.commerceCode ?? '');
            if (!response) {
              alert('No pudimos recuperar datos del comercio ' + params?.commerceCode);
              return;
            }

            await AsyncStorage.setItem(constantsStorage.currentVenue, JSON.stringify(response.commerce));
            setCurrentVenue(response.venue);
            await AsyncStorage.setItem(constantsStorage.currentCommerce, JSON.stringify(response.commerce));
            setCurrentCommerce(response.commerce);

            setCurrentShoppingCart({
              commerce: { id: response.commerce.id, code: response.commerce.code, name: response.commerce.name },
              detail: [],
              processed: false,
              paid: false,
              phone: '',
              isCash: false,
            });

            if (params.status === 'null' && params.payment_id === 'null') {
              navigation.reset({
                index: 0,
                routes: [
                  {
                    name: 'CommerceScreen',
                    params: {
                      venueCode: response.venue.code,
                      commerceCode: response.commerce.code,
                    },
                  },
                ],
              });
              return;
            }
            const order = await getOrder(+(params?.external_reference ?? 0));
            if (!order) {
              alert('Hubo un error al recuperar la orden ' + params?.external_reference);
              return;
            }

            await AsyncStorage.setItem(constantsStorage.guestOrders, JSON.stringify([order]));
            setCurrentOrder(order);
            setUpdatedBasicData(true);
          } catch (error) {
            alert('Hubo un error inesperado para la orden ' + params?.external_reference);
          }
        }
      }
    };
    handleResultPayment();
  }, [resultPayment]);

  React.useEffect(() => {
    if (paymentStatus) {
      setResultPayment({ success: paymentStatus === 'approved' ? true : false });
      setIsLoading(false);
    }
  }, [paymentStatus]);

  React.useEffect(() => {
    if (updatedBasicData) {
      changeResultPayment({ success: true }, false, params?.venueCode);
    }
  }, [updatedBasicData]);

  return (
    <View style={{ flex: 1, justifyContent: 'center', alignSelf: 'center', marginHorizontal: 5 }}>
      {preferenceId && (
        <Wallet
          initialization={{ preferenceId: preferenceId, redirectMode: 'self' }}
          onReady={() => {
            setIsLoading(false);
          }}
        />
      )}
    </View>
  );
};
